import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Image, Button, Space, Tag, Tooltip, Spin, Empty, message, theme as antdTheme } from 'antd';
import { DownloadOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { space, usePrefersReducedMotion } from '@viq-admin/ui';
import type { LeadDetail } from './types';

/**
 * Shell-provided card-image access. OPTIONAL on LeadDetailApi by design: the
 * super-admin console always supplies it; the customer portal supplies it only
 * to a company_admin whose company has card images on
 * (`companies.store_card_images`). Leaving it out is what hides the panel, so
 * do not make it required.
 */
export interface LeadDetailPhotosApi {
  fetchImage: (photoId: string) => Promise<Blob>;
  downloadImage: (photoId: string) => Promise<void>;
}

export interface CardImagesPanelProps {
  photos: LeadDetail['rawphotos'];
  api?: LeadDetailPhotosApi;
  editing?: boolean;
}

function CardImageTile({
  photo,
  api,
  expanded,
}: {
  photo: LeadDetail['rawphotos'][number];
  api: LeadDetailPhotosApi;
  expanded: boolean;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const { token } = antdTheme.useToken();
  const reducedMotion = usePrefersReducedMotion();

  const { data, isLoading, error } = useQuery({
    queryKey: ['cardImage', photo.id],
    queryFn: () => api.fetchImage(photo.id),
    enabled: photo.hasImage,
    staleTime: 5 * 60 * 1000,
    // Bound how long an unused Blob stays cached after going stale. Without
    // this, react-query's default gcTime (5 min) sits on top of the stale
    // window, so every fetched image is retained well beyond its staleTime
    // in long-lived console sessions.
    gcTime: 60_000,
  });

  // Long-lived console sessions open many leads; without revoking, every
  // viewed image would be pinned in memory for the life of the tab.
  useEffect(() => {
    if (!data) return;
    const url = URL.createObjectURL(data);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [data]);

  async function handleDownload() {
    try {
      await api.downloadImage(photo.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : undefined;
      message.error(msg ?? 'Download failed');
    }
  }

  const width = expanded ? 600 : 200;
  const height = expanded ? 420 : 140;
  const transition = reducedMotion ? 'none' : 'width 250ms ease-in-out, height 250ms ease-in-out';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, width }}>
      <div
        data-testid="card-image-frame"
        style={{
          width,
          height,
          transition,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {!photo.hasImage ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No stored image"
            styles={{ image: { height: 40 } }}
          />
        ) : error ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Failed to load" />
        ) : isLoading || !objectUrl ? (
          <Spin />
        ) : (
          <Image
            src={objectUrl}
            alt={`Card image captured ${photo.createdAt}`}
            style={{ maxWidth: width, maxHeight: height, objectFit: 'contain' }}
          />
        )}
      </div>

      <Space size={4} wrap>
        {photo.deletedAt != null && <Tag color="red">Deleted</Tag>}
        {photo.aiScanned === true && <Tag color="green">AI-scanned</Tag>}
        {photo.aiScanned === false && <Tag color="orange">Not AI-scanned</Tag>}
        {photo.toolkitType != null && photo.toolkitType !== '' && (
          <Tooltip title="The badge toolkit this conference had configured when the photo was stored. Whether a model read the photo is shown separately.">
            <Tag color="blue">{`Badge toolkit: ${photo.toolkitType}`}</Tag>
          </Tooltip>
        )}
      </Space>

      <Button
        icon={<DownloadOutlined />}
        size="small"
        disabled={!photo.hasImage}
        onClick={handleDownload}
      >
        Download
      </Button>
    </div>
  );
}

/**
 * Card images for a lead. Renders NOTHING when `api` is undefined — a portal
 * caller who is not a company_admin, or whose company has card images off.
 *
 * Collapsed by default: the scans are bulky, rarely the reason someone opens
 * a lead, and pushed the rest of the detail below the fold. Collapsing also
 * unmounts the tiles rather than hiding them, so no image blob is fetched
 * until an operator actually asks for one. The sibling "Scanned QR" panel
 * stays open — it is small and cheap to render.
 */
export function CardImagesPanel({ photos, api, editing }: CardImagesPanelProps) {
  const [open, setOpen] = useState(false);

  if (!api || photos.length === 0) return null;

  return (
    <Card
      title="Card images"
      size="small"
      extra={
        <Button
          type="text"
          size="small"
          aria-expanded={open}
          icon={open ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide' : `Show ${photos.length} image${photos.length === 1 ? '' : 's'}`}
        </Button>
      }
      // Collapsed, the body would still render as an empty padded strip under
      // the header; hide it outright so the card reads as a single title row.
      styles={open ? undefined : { body: { display: 'none' } }}
    >
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.lg }}>
          {photos.map((p) => (
            <CardImageTile key={p.id} photo={p} api={api} expanded={!!editing} />
          ))}
        </div>
      )}
    </Card>
  );
}
